import React from 'react';
import { useGuitarMatch } from './context/GuitarMatchContext';
import { useAnalysisPipeline } from './context/hooks/useAnalysisPipeline';
import { AudioUploader } from './components/AudioUploader';
import { SmartTrainer } from './components/SmartTrainer/index';
import { LayoutManager } from './components/LayoutManager';
import { NeuralScanner } from './components/SmartTrainer/NeuralScanner';
import { HeroLanding } from './components/HeroLanding';

import {
  AppState,
  RecordingMetadata,
  MatchType,
  YouTubeReference,
  TimeSignature
} from './types';

import { saveSession, computeUserStatsFromHistory } from './services/storageService';

export default function AppContent() {
  const {
    appState,
    setAppState,
    result,
    targetBpm,
    setTargetBpm,
    manualGrid,
    setManualGrid,
    timeSignature,
    setTimeSignature,
    refFile,
    setRefFile,
    studentFile,
    youtubeMasterRef,
    setYoutubeMasterRef,
    resetAll,
    setResult,
    lang,
    currentLevelId,
    globalCalibrationOffset,
    matchType,
    setMatchSourceName,
    setActiveOverlay,
    incrementAnalysisCount,
    soundProfile,
    activeMission,
    updateUserProfile
  } = useGuitarMatch();

  const pipeline = useAnalysisPipeline({
    onSuccess: async (res) => {
      setResult(res);
      setAppState(AppState.RESULTS);
      incrementAnalysisCount();

      // ✅ Persist + recompute stats (NO-SIM: basé sur l'historique)
      if (res && studentFile) {
        try {
          const finalRefFile =
            refFile || new File(['YT_REF'], 'youtube_reference.txt', { type: 'text/plain' });

          const saved = await saveSession(finalRefFile, studentFile, res);

          if (saved) {
            const stats = await computeUserStatsFromHistory();

            updateUserProfile({
              stats: {
                avgScore: stats.avgScore,
                totalMatches: stats.totalMatches,
                wins: stats.wins,
                bestRank: stats.bestRank,
                levelXP: stats.levelXP,
                timingSkill: stats.timingSkill,
                soulSkill: stats.soulSkill,
                cleanSkill: stats.cleanSkill
              }
            });
          }
        } catch (e) {
          console.error('[STORAGE] Critical persistence failure:', e);
        }
      }
    },
    onError: (err) => alert(`Erreur d'analyse: ${err}`)
  });

  const handleStartAnalysis = async (
    fileOrRef: File | YouTubeReference,
    metadata?: RecordingMetadata,
    qualityWarning?: string,
    matchTypeOverride?: MatchType
  ) => {
    // ✅ Pipeline actuel: analyse uniquement si File
    if (fileOrRef instanceof File) {
      pipeline.run({
        refFile,
        studentFile: fileOrRef,
        targetBpm,
        latencyMs: globalCalibrationOffset,
        lang,
        grid: manualGrid,
        levelId: currentLevelId,
        matchType: matchTypeOverride || matchType,
        timeSignature,
        soundProfile,
        metadata,
        qualityWarning,
        activeMission
      });
    } else {
      // (si tu veux supporter YouTubeReference dans pipeline plus tard)
      console.warn('[ANALYSIS] YouTubeReference provided but pipeline expects File. Ignored for now.');
    }
  };

  const insertToGrid = (token: string) => {
    const current = manualGrid.trim();
    const separator = current === '' || current.endsWith('|') ? '' : ' ';
    setManualGrid(current + separator + token + ' ');
  };

  const handleLoopToLength = (targetBars: number) => {
    const bars = manualGrid
      .split('|')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (bars.length === 0) return;

    let newGrid = '';
    for (let i = 0; i < targetBars; i++) {
      const barContent = bars[i % bars.length];
      newGrid += barContent + (i < targetBars - 1 ? ' | ' : '');
    }
    setManualGrid(newGrid);
  };

  return (
    <LayoutManager
      onSongSelect={(title, grid) => {
        setManualGrid(grid.join(' '));
        setMatchSourceName(title);
        setAppState(AppState.ANALYZING);
      }}
      onTeacherSelect={() => {}}
      onStemImport={(fileOrRef: File | YouTubeReference, bpm: number, grid: string) => {
        if (fileOrRef instanceof File) {
          setRefFile(fileOrRef);
          setYoutubeMasterRef(null);
        } else {
          setYoutubeMasterRef(fileOrRef);
          setRefFile(null);
          setMatchSourceName('YouTube Play-Along');
        }

        setTargetBpm(bpm);
        setManualGrid(grid);
        setAppState(AppState.ANALYZING);
      }}
      startAnalysis={handleStartAnalysis}
    >
      <div className="w-full h-full flex flex-col relative overflow-hidden">
        {appState === AppState.IDLE && (
          <HeroLanding onStart={() => setAppState(AppState.ANALYZING)} lang={lang} />
        )}

        {pipeline.isProcessing && <NeuralScanner stage={pipeline.stage} lang={lang} />}

        {appState === AppState.ANALYZING && !pipeline.isProcessing && (
          <div className="z-10 flex-1 flex flex-col max-w-6xl mx-auto w-full p-6 md:p-12 gap-8 animate-fade-in overflow-y-auto">
            <div className="flex justify-between items-center bg-white/[0.02] p-4 rounded-[32px] border border-white/10 backdrop-blur-3xl shadow-xl">
              <div className="flex gap-2">
                <button
                  onClick={() => setAppState(AppState.IDLE)}
                  className="px-6 py-3 rounded-2xl bg-white/5 border border-white/10 text-[10px] font-black uppercase text-slate-400"
                >
                  Back
                </button>
              </div>

              <button
                onClick={() => setActiveOverlay('STEM')}
                className="px-8 py-3 rounded-2xl bg-red-600 text-white font-black uppercase text-[10px]"
              >
                Import Source
              </button>
            </div>

            <div className="w-full space-y-6">
              <div className="flex flex-wrap gap-4 px-2 items-center">
                <button
                  onClick={() => insertToGrid('|')}
                  className="px-6 py-3 bg-smart-accent text-white rounded-xl font-black text-xs shadow-lg hover:scale-110 active:scale-95 transition-all border border-white/10"
                >
                  MESURE [ | ]
                </button>

                <div className="flex bg-black/40 p-1 rounded-2xl border border-white/10 items-center gap-1">
                  <span className="text-[8px] font-black text-slate-500 uppercase px-3 italic">
                    Loop Cycle:
                  </span>
                  {[4, 8, 16, 32].map((len) => (
                    <button
                      key={len}
                      onClick={() => handleLoopToLength(len)}
                      className="px-4 py-2 rounded-xl text-[10px] font-black text-white hover:bg-white/10 transition-all border border-transparent hover:border-white/5"
                    >
                      {len}
                    </button>
                  ))}
                </div>

                <div className="w-px h-8 bg-white/10 mx-2 self-center"></div>

                {['G', 'C', 'D', 'Am', 'Em', 'E', 'A', 'F'].map((chord) => (
                  <button
                    key={chord}
                    onClick={() => insertToGrid(chord)}
                    className="px-4 py-3 bg-white/5 text-slate-300 rounded-xl font-bold text-[10px] hover:bg-white hover:text-black transition-all border border-white/5"
                  >
                    {chord}
                  </button>
                ))}

                <button
                  onClick={() => setManualGrid('')}
                  className="px-4 py-3 bg-red-900/20 text-red-400 rounded-xl font-black text-[9px] uppercase tracking-widest ml-auto"
                >
                  Effacer
                </button>
              </div>

              <div className="bg-white/[0.04] border border-white/10 rounded-[40px] p-4 flex flex-col md:flex-row items-center gap-6 shadow-2xl">
                <div className="flex bg-black/40 p-1.5 rounded-[24px] border border-white/5">
                  {(['4/4', '3/4', '6/8'] as TimeSignature[]).map((sig) => (
                    <button
                      key={sig}
                      onClick={() => setTimeSignature(sig)}
                      className={`px-5 py-2.5 rounded-[18px] text-[10px] font-black uppercase transition-all ${
                        timeSignature === sig ? 'bg-white text-black' : 'text-slate-500'
                      }`}
                    >
                      {sig}
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  value={manualGrid}
                  onChange={(e) => setManualGrid(e.target.value)}
                  placeholder="Am F | C G"
                  className="flex-1 bg-transparent text-white font-black italic text-4xl outline-none placeholder:text-white/5"
                />
              </div>

              <div className="bg-white/5 border border-white/10 rounded-[32px] px-10 py-6 flex items-center justify-center gap-12 backdrop-blur-xl w-fit mx-auto shadow-xl">
                <button
                  onClick={() => setTargetBpm(targetBpm - 1)}
                  className="w-12 h-12 rounded-full bg-white/5 text-white text-2xl font-black hover:bg-white hover:text-black transition-all"
                >
                  −
                </button>

                <div className="flex flex-col items-center">
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                    Tempo
                  </span>
                  <span className="text-6xl font-black italic text-smart-gold tabular-nums drop-shadow-lg">
                    {targetBpm}
                  </span>
                </div>

                <button
                  onClick={() => setTargetBpm(targetBpm + 1)}
                  className="w-12 h-12 rounded-full bg-white/5 text-white text-2xl font-black hover:bg-white hover:text-black transition-all"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8 pb-12">
              <AudioUploader
                id="upload-zone-ref"
                label="Neural Master Target"
                color="gold"
                onFileSelect={(f) => {
                  if (f instanceof File) setRefFile(f);
                }}
                isTeacher={true}
                externalFile={refFile}
                youtubeRef={youtubeMasterRef}
              />

              <AudioUploader
                id="upload-zone-stu"
                label="Votre Session Live"
                color="accent"
                onFileSelect={handleStartAnalysis}
                masterReferenceFile={refFile}
              />
            </div>
          </div>
        )}

        {appState === AppState.RESULTS && result && (
          <div className="z-30 w-full h-full p-4 md:p-8 animate-portal-reveal">
            <SmartTrainer
              result={result}
              onReAnalyze={() => setAppState(AppState.ANALYZING)}
              onHome={resetAll}
              currentLevelId={result.coaching_level || currentLevelId}
              lang={lang}
            />
          </div>
        )}
      </div>
    </LayoutManager>
  );
}
